import React, { useEffect, useState } from "react";
import { Box, SvgIcon, Collapse, Divider } from "@mui/material";
import {
  LuSlidersVertical,
  MdTaskAlt,
  LuChevronDown,
  LuSearch,
} from "@/Components/Icons/Icons";
import DateSelector from "@/Components/Form/DateSelector";
import DateTimePicker from "@/Components/Form/DateTimePicker";
import InputTextField from "@/Components/Form/InputTextField";
import InputDropdown from "@/Components/Form/InputDropdown";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { importHookMap } from "@/redux/importHookMap";
import { useUser } from "@/Context/UserContext";
import { mapImporterRequest } from "@/redux/mappers/Import/CreateNewJob/GeneralPageMaper";
import {
  fillingmodeOptions,
  jobOwnerList,
  FllingModeList,
  CustomHouse,
  ImportBEType,
} from "@/Constant/Export/CreateNewJob/FilterBox.jsx";
import dayjs from "dayjs";
import { BiSolidError } from "react-icons/bi";
import { LuCheck } from "react-icons/lu";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import {
  transportMode,
  importdefaultValues,
  customsHouse
} from "../../../Constant/Common/Common";
import { useSbType } from "@/Context/sbTypeContext";

export default function FilterBar({
  data = {},
  compKey = "general",
  importerMasterId,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [taskValue, setTaskValue] = useState(""); // state for Task input
  const [selectedOrg, setSelectedOrg] = useState(""); // state for Task input
  const useApiHooks = importHookMap["importer"];
  const { create, update, fetch } = useApiHooks ? useApiHooks() : {};
  const [loading, setLoading] = useState(false);
  const { sbType, setSbType } = useSbType();
  const { transportmode, setTransportMode } = useSbType();
  const { customsMode, setCustomsMode } = useSbType();
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isExporterAvailable, setIsExporterAvailable] = useState(false);
  const formData = useUser((state) => state.formData);
  const setFormData = useUser((state) => state.setFormData);
  const setImporterMasterId = useUser((state) => state.setImporterMasterId);
  const { setImportTrigger} = useUser();

  const handleToggleAdvanced = () => {
    setShowAdvanced((prev) => !prev);
  };

  const handleTaskChange = (e) => {
    setTaskValue(e.target.value);
  };

  const handleChange = (field, value) => {
    let formattedValue = value;

    // if it's a dayjs object, save ISO string
    if (value && value.$d) {
      const finalValue =
        dayjs(value)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
      formattedValue = finalValue;
    }

    setFormData((prev) => ({
      ...prev,
      [compKey]: {
        ...prev[compKey],
        [field]: formattedValue,
      },
    }));
  };
  
  useEffect(() => {
    // run only when both local and incoming data are empty
    const isDataEmpty = !data || Object.keys(data).length === 0;
    const hasFormData =
      formData?.[compKey] && Object.keys(formData[compKey]).length > 0;

    if (isDataEmpty && !hasFormData) {
      setFormData((prev) => ({
        ...prev,
        [compKey]: {
          ...importdefaultValues,
        },
      }));

      setTransportMode(importdefaultValues.importJobTransportMode);
      setCustomsMode(importdefaultValues.importJobCustomHouse);
      setSbType(importdefaultValues.importJobBEType);
    }
  }, [data, compKey]);

  const removeEmpty = (obj) => {
    if (Array.isArray(obj)) {
      return obj
        .map((item) => removeEmpty(item))
        .filter((item) => !(item === undefined));
    } else if (obj && typeof obj === "object") {
      // ✅ If the object has a "value" key, return just that
      if ("value" in obj) {
        return removeEmpty(obj.value);
      }

      const cleaned = Object.fromEntries(
        Object.entries(obj)
          .map(([k, v]) => [k, removeEmpty(v)])
          .filter(([_, v]) => v !== undefined) // remove empty fields
      );

      // ✅ If object became empty → keep it as {}
      return Object.keys(cleaned).length > 0 ? cleaned : {};
    }

    // ✅ Remove only invalid primitives
    if (obj === "" || obj === null || obj === "-") {
      return undefined; // field dropped
    }

    return obj;
  };
  const flattenData = (obj) => {
    if (!obj || typeof obj !== "object") return obj;

    // For objects with 'value' key, return only the value
    if ("value" in obj) return obj.value;

    if ("id" in obj) return obj.id;

    // Otherwise, recurse for nested objects
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, flattenData(v)])
    );
  };

  useEffect(() => {
    const isImporterAvailable = !!formData?.general?.importerId;
    setIsExporterAvailable(isImporterAvailable);
  }, []);

  const handleApply = async () => {
    setLoading(true);
    try {
      let data = mapImporterRequest(formData.general);
      data = removeEmpty(flattenData(data));

      if (data.ImportRequest?.importGeneral) {
        data.ImportRequest.importGeneral = {};
      }

      let payload = {
        id: importerMasterId,
        processingId: formData?.general?.processingId || null,
        ...data,
      };

      let response;
      if (importerMasterId && update) {
        response = await update(payload).unwrap();
      } else if (create) {
        response = await create(payload).unwrap();
      } else {
        console.warn("No valid API method found");
        return;
      }

      // ✅ Always set the dialog
      const isSuccess =
        response?.statusCode === 200 || response?.data?.statusCode === 200;

      setSuccessDialogConfig({
        label: isSuccess ? "Success" : "Failure",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {response?.data?.message ||
              response?.message ||
              "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        IconComponent: isSuccess ? LuCheck : BiSolidError,
        status: isSuccess ? "success" : "error",
      });

      setShowSuccessDialog(true);

      // If successful and create → fetch updated data
      if (isSuccess && response?.data && !importerMasterId && fetch) {
        const fetchPayload = { id: response.data };
        const fetchResponse = await fetch(fetchPayload);
        setFormData((prev) => ({
          ...prev,
          [compKey]: {
            ...(prev[compKey] || {}),
            ...fetchResponse,
          },
        }));
        setImporterMasterId(response.data);
      }
      setImportTrigger((prev)=>prev+1)
    } catch (error) {
      console.error("API call failed:", error);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {error?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
      setShowAdvanced(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "var(--gray-0)",
        border: "1px solid var(--brand-200)",
        borderRadius: 2,
        py: 1,
      }}
    >
      {/* Primary Filter Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "var(--v-gap-sm-md)",
          mb: showAdvanced ? 1 : "var(--v-gap-xs)",
          px: 2,
          // ml: 0.1,
          // mt : "var(--v-gap-xs)"
        }}
      >
        {/* Search Box */}
        <InputTextField
          label="Job Number"
          disabled
          value={data?.importJobNumber}
          width="20%"
          placeholder="New Entry"
        />
        <DateSelector
          inputSize="medium"
          label="Job Date"
          width="20%"
          value={data.importJobDate || dayjs()}
          onChange={(val) => handleChange("exportJobDate", val)}
          maxDateToToday={true} 
        />
        <DateTimePicker
          inputSize="medium"
          label="Job Received On"
          // height="40px"
          // sx={{ mb: 3.8}}
          width="23%"
          maxDateToToday={true} 
          value={
            data.importJobReceivedOn
              ? (data.importJobReceivedOn, "DD:MM:YYYY HH:mm")
              : dayjs()
          }
          onChange={(val) => handleChange("importJobReceivedOn", val)}
        />
        <InputDropdown
          placeholder="Select Owner"
          inputSize="medium"
          label="Job Owner"
          options={jobOwnerList}
          width="15%"
          value={data.importJobOwner || ""}
          onChange={(val) => handleChange("importJobOwner", val)}
        />
        {/* Advanced Filter Button */}
        <Box sx={{ flexBasis: "15%" }}>
          <OutLinedButton
            variant="bodySmallMedium"
            label="Expand"
            startIcon={
              <SvgIcon sx={{ color: "var(--brand-600)" }}>
                <LuSlidersVertical fontSize={"25px"} />
              </SvgIcon>
            }
            sx={{
              border: "1px solid var(--brand-600)",
              color: "var(--brand-600)",
              width: 200,
              mt: 3.6,
              backgroundColor: "var(--gray-0)",
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "var(--gray-0)",
              },
            }}
            onClick={handleToggleAdvanced}
          />
        </Box>
      </Box>

      {/* Advanced Filters Section */}
      <Collapse in={showAdvanced}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: "column",
          }}
        >
          {/* Row1 */}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              px: 2,
            }}
          >
            <InputDropdown
              label="Filling mode"
              placeholder="Online"
              dropdownIconName="LuChevronDown"
              width="15%"
              options={fillingmodeOptions}
              value={data?.importJobFillingMode || null}
              onChange={(val) => handleChange("importJobFillingMode", val)}
            />

            <InputDropdown
              placeholder="All"
              dropdownIconName="LuChevronDown"
              width="22%"
              options={FllingModeList}
              // sx={{mt:-0.2}}
              onChange={(val) => handleChange("importJobFillingModeType", val)}
              value={data?.importJobFillingModeType || null}
            />
            {/* <InputDropdown
              label="Transport Mode"
              placeholder="Air"
              dropdownIconName="LuChevronDown"
              width="15%"
              value={data?.importJobTransportMode || null}
              onChange={(val) => handleChange("importJobTransportMode", val)}
              options={transportMode}
            /> */}

            <InputDropdown
              label="Transport Mode"
              placeholder="Transport Mode"
              width="13%"
              value={data.importJobTransportMode || ""}
              disabled={data.importJobTransportMode && data.exporterJobNumber} // disable if API data exists
              onChange={(val) => {
                setTransportMode(val);
                handleChange("importJobTransportMode", val);
                console.log(
                  customsMode,
                  "customsMode",
                  customsHouse[val].some((opt) => opt.value === customsMode)
                );
                if (
                  !customsHouse[val]?.some((opt) => opt.value === customsMode)
                ) {
                  if (val === "air") {
                    handleChange("exportCustomHouse", "Bengaluru Air Cargo");
                    handleChange("exportLoadingPort", "Bengaluru Air Cargo");
                    setCustomsMode("Bengaluru Air Cargo");
                  } else {
                    handleChange("exportCustomHouse", "Chennai Sea");
                    handleChange("exportLoadingPort", "Chennai Sea");
                    setCustomsMode("Chennai Sea");
                  }
                }
              }}
              options={transportMode}
            />

            {/* <InputDropdown
              label="Customs House"
              placeholder="All"
              dropdownIconName="LuChevronDown"
              width="25%"
              value={data?.importJobCustomHouse || null}
              onChange={(val) => handleChange("importJobCustomHouse", val)}
              options={CustomHouse}
            /> */}
                        <InputDropdown
              label="Customs House"
              placeholder="Customs House"
              options={
                customsHouse[data.importJobTransportMode] || customsHouse.air
              }
              value={data.importJobCustomHouse || ""}
              onChange={(val) => {
                handleChange("importJobCustomHouse", val);
                setCustomsMode(val);
              }}
              width="25%"
            />
            <InputDropdown
              label="BE Type"
              placeholder="Home"
              dropdownIconName="LuChevronDown"
              width="15%"
              onChange={(val) => handleChange("importJobBEType", val)}
              value={data?.importJobBEType || null}
              options={ImportBEType}
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          {/* footer */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              px: 2,
              justifyContent: "end",
              // mb : 1
            }}
          >
            <OutLinedButton
              variant={"bodySmallStrong"}
              label={"Close"}
              width="10%"
              // height="20%"
              height={40}
              onClick={() => setShowAdvanced(false)}
            />
            <BlackButton label={"Save"} width="10%" onClick={handleApply} />
          </Box>
        </Box>
      </Collapse>
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        {...successDialogConfig}
      />
    </Box>
  );
}
